import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { HiXMark } from "react-icons/hi2";
import { TrainingType } from "../../types/activity";
import {
  Session,
  PeriodisationType,
  PERIODISATION_OPTIONS,
} from "../../types/post";
import { trainingTypeService } from "../../services/trainingTypeService";
import { sessionService } from "../../services/sessionService";
import { asDisplayNameOrNull } from "../../utils/displayName";
import {
  canChangeSessionDate,
  getDateChangeBlockedReason,
} from "../../utils/sessionRules";
import { TEXT_BUTTON_CLASS } from "../../utils/buttonStyles";

interface EditSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: (Session & { id: string }) | null;
  onSessionUpdated?: (sessionId: string) => void;
  /**
   * Date-only mode (plan 3c "Change date"): a compact sheet with just the date
   * field, no read-only coach fields. Eligibility follows decision 2 via
   * `canChangeSessionDate`.
   */
  dateOnly?: boolean;
  /** Whether wearable activity data is attached (blocks completed moves). */
  hasWearableActivity?: boolean;
  /**
   * When provided, the modal hands the validated date change to the caller
   * instead of writing it itself, so a page that keeps its own session state
   * (Schedule) can do the optimistic update and rollback in one place.
   */
  onSaveDate?: (sessionId: string, newPlannedDate: string) => Promise<void>;
}

// Move a timestamp onto a new calendar date (YYYY-MM-DD) while keeping its
// original local time of day. Used to keep a completed session's start/end
// timestamps aligned with an edited completion date.
export const shiftDateKeepingTime = (iso: string, newDate: string): string => {
  const original = new Date(iso);
  if (isNaN(original.getTime())) return iso;
  const [year, month, day] = newDate.split("-").map(Number);
  if (!year || !month || !day) return iso;
  const shifted = new Date(original);
  shifted.setFullYear(year, month - 1, day);
  return shifted.toISOString();
};

/**
 * Firestore patch for moving a session to `newPlannedDate`. Completed
 * sessions also carry their start/end timestamps onto the new day so the
 * duration and "Completed" time stay correct.
 */
export const buildDateChangeUpdates = (
  session: Pick<Session, "status" | "startTime" | "endTime">,
  newPlannedDate: string,
): Partial<Session> => {
  const updates: Partial<Session> = { plannedDate: newPlannedDate };
  if (session.status === "complete") {
    if (session.startTime) {
      updates.startTime = shiftDateKeepingTime(session.startTime, newPlannedDate);
    }
    if (session.endTime) {
      updates.endTime = shiftDateKeepingTime(session.endTime, newPlannedDate);
    }
  }
  return updates;
};

const EditSessionModal: React.FC<EditSessionModalProps> = ({
  isOpen,
  onClose,
  session,
  onSessionUpdated,
  dateOnly = false,
  hasWearableActivity = false,
  onSaveDate,
}) => {
  const { currentUser, userData } = useAuth();

  // Form state
  const [plannedDate, setPlannedDate] = useState<string>("");
  const [sessionName, setSessionName] = useState<string>("");
  const [periodisation, setPeriodisation] = useState<PeriodisationType | "">(
    "",
  );

  // Loading and error states
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);

  // Data
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);

  // Initialize form with session data
  useEffect(() => {
    if (session && isOpen) {
      setPlannedDate(session.plannedDate);
      setSessionName(asDisplayNameOrNull(session.name) || "");
      setPeriodisation(session.periodisation || "");
    }
  }, [session, isOpen]);

  // Fetch training types for the read-only session type display
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid || !isOpen) return;
      if (dateOnly) {
        setIsLoadingData(false);
        return;
      }

      try {
        setIsLoadingData(true);
        const types = await trainingTypeService.fetchTrainingTypes();
        setTrainingTypes(types);
      } catch (error) {
        console.error("Error fetching training types:", error);
        setError("Failed to load session types");
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [currentUser?.uid, isOpen, dateOnly]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  const handleUpdateSession = async () => {
    if (!currentUser?.uid || !session) {
      setError("Session not found");
      return;
    }

    if (!plannedDate) {
      setError(
        session.status === "complete"
          ? "Please select a completed date"
          : "Please select a planned date",
      );
      return;
    }

    if (
      plannedDate !== session.plannedDate &&
      !canChangeSessionDate(session, { hasWearableActivity })
    ) {
      setError(
        getDateChangeBlockedReason(session, { hasWearableActivity }) ||
          "This session cannot be moved",
      );
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Athletes can only reschedule a session's date. Session name, type and
      // periodisation are set by the coach and remain read-only here.
      if (onSaveDate) {
        await onSaveDate(session.id, plannedDate);
      } else {
        await sessionService.updateSession(
          currentUser.uid,
          session.id,
          buildDateChangeUpdates(session, plannedDate),
        );
      }

      console.log("Session updated successfully");

      if (onSessionUpdated) {
        onSessionUpdated(session.id);
      }

      onClose();
    } catch (error) {
      console.error("Error updating session:", error);
      setError(
        error instanceof Error ? error.message : "Failed to update session",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !session) return null;

  // Read-only display values (set by the coach, not editable by the athlete)
  const sessionTypeName =
    trainingTypeService.getSessionTrainingTypeName(session, trainingTypes) ||
    "None";
  const periodisationLabel = periodisation
    ? PERIODISATION_OPTIONS.find((option) => option.value === periodisation)
        ?.label || periodisation
    : "None";

  const isCompleted = session.status === "complete";
  const dateFieldLabel = isCompleted ? "Completed Date" : "Planned Date";
  const blockedReason = getDateChangeBlockedReason(session, {
    hasWearableActivity,
  });

  if (dateOnly) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-end sm:items-center justify-center z-[10000] sm:p-4"
        onClick={loading ? undefined : onClose}
      >
        <div
          className="bg-base-100 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-date-title"
        >
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <div className="min-w-0">
              <h2
                id="change-date-title"
                className="text-lg text-base-content font-heading"
              >
                Change <strong>date</strong>
              </h2>
              <p className="text-xs text-base-content/60 font-grotesk truncate mt-0.5">
                {sessionName || "Session"}
                {isCompleted ? " · completed" : ""}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              aria-label="Close"
              className="p-2 text-base-content/50 hover:text-base-content/80 rounded-full hover:bg-base-200 transition-colors disabled:opacity-50"
            >
              <HiXMark className="text-xl" />
            </button>
          </div>

          <div className="px-5 space-y-3">
            <div className="bg-base-200 border border-base-300 p-3 rounded-xl focus-within:border-base-content/60">
              <label className="block text-xs font-medium text-base-content/50 mb-1 font-grotesk">
                {dateFieldLabel}
              </label>
              <input
                type="date"
                value={plannedDate}
                onChange={(e) => setPlannedDate(e.target.value)}
                className="bg-transparent text-[16px] w-full focus:outline-none border-0 p-0 font-geist tabular-nums cursor-pointer text-base-content"
                disabled={loading || !!blockedReason}
                style={{
                  colorScheme: "light",
                  WebkitAppearance: "none",
                  MozAppearance: "textfield",
                }}
                onClick={(e) => {
                  e.currentTarget.showPicker?.();
                }}
              />
            </div>
            {isCompleted && !blockedReason && (
              <p className="text-xs text-base-content/60 font-grotesk">
                Start and end times keep their time of day on the new date.
              </p>
            )}
            {blockedReason && (
              <p className="text-xs text-warning font-grotesk">{blockedReason}</p>
            )}
            {error && (
              <div className="bg-error/10 border border-error/30 rounded-xl p-3">
                <p className="text-sm text-error">{error}</p>
              </div>
            )}
          </div>

          <div className="px-5 pt-4 pb-5 flex gap-3 justify-between">
            <button
              onClick={onClose}
              disabled={loading}
              className={TEXT_BUTTON_CLASS}
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateSession}
              disabled={
                loading ||
                !plannedDate ||
                !!blockedReason ||
                plannedDate === session.plannedDate
              }
              className="flex-1 bg-primary text-primary-content rounded-xl py-3 px-5 min-h-11 font-grotesk font-semibold hover:bg-primary/90 disabled:bg-primary/30 disabled:text-primary-content/50 disabled:cursor-not-allowed"
            >
              {loading ? "Saving..." : "Save date"}
            </button>
          </div>
          <div className="pb-safe" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[10000] sm:p-4">
      <div
        className="bg-base-100 w-full h-full md:rounded-xl md:shadow-xl sm:max-w-2xl md:h-auto md:min-h-[70vh] md:max-h-[70vh] flex flex-col relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="flex bg-base-100 items-center justify-between p-8 pb-0 flex-shrink-0 md:rounded-t-xl">
          <h2 className="text-xl text-base-content font-grotesk">
            Edit <strong>Session</strong>
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 text-base-content/50 hover:text-base-content/80 rounded-full hover:bg-base-200 transition-colors disabled:opacity-50"
          >
            <HiXMark className="text-xl" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 px-8 space-y-4 bg-base-100">
          {/* Session Name (read-only - set by coach) */}
          <div className="bg-base-200 border-base-200 border-2 p-3 rounded-md">
            <label className="block text-xs font-medium text-base-content/50 mb-1 font-grotesk">
              Session Name
            </label>
            <div className="text-[16px] sm:text-sm font-sans text-base-content/70">
              {sessionName || "None"}
            </div>
          </div>

          {/* Date field: the only editable field for athletes. For completed
              sessions this is the completion date. */}
          <div className="bg-base-100 border-base-200 border-2 p-3 rounded-md focus-within:ring-1 focus-within:ring-base-content/80 focus-within:border-base-content/80">
            <label className="block text-xs font-medium text-base-content/50 mb-1 font-grotesk">
              {dateFieldLabel}
            </label>
            <input
              type="date"
              value={plannedDate}
              onChange={(e) => setPlannedDate(e.target.value)}
              className="bg-transparent text-[16px] sm:text-sm w-full focus:outline-none border-0 p-0 font-sans cursor-pointer text-base-content"
              disabled={loading}
              style={{
                colorScheme: "light",
                WebkitAppearance: "none",
                MozAppearance: "textfield",
              }}
              onClick={(e) => {
                e.currentTarget.showPicker?.();
              }}
            />
          </div>

          {/* Session Type (read-only - set by coach) */}
          <div className="bg-base-200 border-base-200 border-2 p-3 rounded-md">
            <label className="block text-xs font-medium text-base-content/50 mb-1 font-grotesk">
              Session Type
            </label>
            <div className="text-[16px] sm:text-sm font-sans text-base-content/70">
              {isLoadingData ? "Loading..." : sessionTypeName}
            </div>
          </div>

          {/* Periodisation (read-only - set by coach) */}
          <div className="bg-base-200 border-base-200 border-2 p-3 rounded-md">
            <label className="block text-xs font-medium text-base-content/50 mb-1 font-grotesk">
              Periodisation
            </label>
            <div className="text-[16px] sm:text-sm font-sans text-base-content/70">
              {periodisationLabel}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="pb-20 px-8 mt-auto flex gap-3 justify-between bg-base-100">
          <button
            onClick={onClose}
            disabled={loading}
            className={TEXT_BUTTON_CLASS}
          >
            Cancel
          </button>

          <button
            onClick={handleUpdateSession}
            disabled={loading || !plannedDate}
            className="bg-primary text-primary-content rounded-full py-3 px-6 min-h-11 font-grotesk font-semibold hover:bg-primary/90 disabled:bg-primary/30 disabled:text-primary-content/50 disabled:cursor-not-allowed"
          >
            {loading ? "Updating..." : "Update Session"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSessionModal;
